import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full.png';
import {
  fetchProjectionSummary,
  fetchProjectionZips,
  fetchProjectionZipDemographics,
  fetchProjectionShare,
} from '@src/api/actions/projections';
import ProjectionZipMap from './components/ProjectionZipMap';

const scenarioMultipliers = { low: 0.9, base: 1, high: 1.2 };

const formatClientName = (slug) => {
  if (!slug) return '';
  return String(slug)
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const parseZips = (param) => {
  if (!param) return [];
  try {
    const parsed = JSON.parse(param);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // fall back to text parsing
    return String(param)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [zip = '', territory = ''] = line.split(/[,|\t]/);
        let normalizedZip = zip.trim();
        if (/^\d{4}$/.test(normalizedZip)) normalizedZip = `0${normalizedZip}`;
        return { zip: normalizedZip, territory: territory.trim() };
      })
      .filter((row) => row.zip);
  }
  return [];
};

const parseBudgets = (param) => {
  if (!param) return {};
  try {
    const parsed = JSON.parse(param);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const formatRate = (value, decimals = 0) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${(num * 100).toFixed(decimals)}%`;
};

const formatPercent = (value, decimals = 0) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${num.toFixed(decimals)}%`;
};

function PublicProjectionsClientView() {
  const { clientSlug = 'new-client' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);
  const [projection, setProjection] = useState({ scenario: 'base', summary: null, zipRows: [] });
  const [demographics, setDemographics] = useState({ territories: [], totals: null, meta: null, features: [] });
  const [verticals, setVerticals] = useState({ selections: [], budgets: {} });
  const [zipRows, setZipRows] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [shareError, setShareError] = useState('');
  const [token, setToken] = useState(() => searchParams.get('token') || '');
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  const handleFetch = useCallback(
    (payloadOverride = null) => {
      const payload =
        payloadOverride ||
        (() => {
          if (!verticals.selections.length || !zipRows.length) return null;
          const zipList = Array.from(new Set(zipRows.map((row) => row.zip))).filter(Boolean);
          return {
            verticals: verticals.selections,
            zips: zipList,
            zip_rows: zipRows,
            budget: Object.values(verticals.budgets || {}).reduce((sum, val) => sum + (Number(val) || 0), 0),
            vertical_budgets: verticals.budgets || {},
            scenario: projection.scenario || 'base',
            vertical: verticals.selections[0] || '',
            client_slug: clientSlug,
          };
        })();
      if (!payload) return;
      setIsLoadingProjection(true);
      Promise.all([fetchProjectionSummary(payload), fetchProjectionZips(payload), fetchProjectionZipDemographics(payload)])
        .then(([summaryResp, zipResp, demoResp]) => {
          setProjection((prev) => ({
            ...prev,
            summary: summaryResp?.data?.summary || null,
            zipRows: zipResp?.data?.rows || [],
          }));
          setDemographics({
            territories: demoResp?.data?.territories || [],
            totals: demoResp?.data?.totals || null,
            meta: demoResp?.data?.meta || null,
            features: demoResp?.data?.features || [],
          });
          setHasFetchedOnce(true);
        })
        .catch(() => {
          setProjection((prev) => ({ ...prev, summary: null, zipRows: [] }));
          setDemographics({ territories: [], totals: null, meta: null, features: [] });
        })
        .finally(() => setIsLoadingProjection(false));
    },
    [clientSlug, projection.scenario, verticals.budgets, verticals.selections, zipRows]
  );

  useEffect(() => {
    const paramToken = searchParams.get('token') || '';
    if (paramToken && paramToken !== token) {
      setToken(paramToken);
      setHasFetchedOnce(false);
    }
  }, [searchParams, token]);

  useEffect(() => {
    if (!token) {
      setShareError('Missing share token.');
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setShareError('');
    setIsLoadingProjection(true);
    fetchProjectionShare(token)
      .then((resp) => {
        const payload = resp?.data?.payload || {};
        const storedSummary = resp?.data?.projection_summary;
        const storedZipRows = resp?.data?.projection_zip_rows;
        const storedDemographics = resp?.data?.projection_demographics;
        const selections = payload.verticals || [];
        const budgets = payload.vertical_budgets || {};
        const zrows = payload.zip_rows || [];
        const fallbackName = formatClientName(payload.client_slug || clientSlug);
        const clientPayload = {
          name: payload.client_name || resp?.data?.client_name || fallbackName,
          client_logo_url: payload.client_logo_url || resp?.data?.client_logo_url,
          client_logo_file: payload.client_logo_file || resp?.data?.client_logo_file,
          slug: payload.client_slug || clientSlug,
        };
        const latestScenario =
          typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('scenario') : null;
        setVerticals({ selections, budgets });
        setZipRows(zrows);
        setClient((prev) => ({ ...(prev || {}), ...clientPayload }));
        setProjection((prev) => ({
          ...prev,
          scenario: latestScenario || payload.scenario || prev.scenario,
        }));
        setInitialized(true);
        if (storedSummary || storedDemographics) {
          setProjection((prev) => ({
            ...prev,
            summary: storedSummary || prev.summary,
            zipRows: storedZipRows || prev.zipRows,
          }));
          setDemographics({
            territories: storedDemographics?.territories || [],
            totals: storedDemographics?.totals || null,
            meta: storedDemographics?.meta || null,
            features: storedDemographics?.features || [],
          });
          setHasFetchedOnce(true);
          setIsLoadingProjection(false);
        } else {
          handleFetch(payload);
        }
      })
      .catch(() => {
        setShareError('Could not load shared projections.');
      })
      .finally(() => setIsLoadingProjection(false));
  }, [handleFetch, token]);

  useEffect(() => {
    if (!initialized) return;
    if (!verticals.selections.length || !zipRows.length) return;
    if (hasFetchedOnce) return;
    handleFetch();
  }, [handleFetch, hasFetchedOnce, initialized, verticals.selections.length, zipRows.length]);

  const projectionResults = useMemo(() => {
    const summary = projection.summary;
    if (!summary) return { rows: [], totals: null, assumptions: {} };

    const scenarioKey = projection.scenario || 'base';
    const multiplier = scenarioMultipliers[scenarioKey] || 1;
    const assumptions = summary.assumptions || {};
    const rows = (summary.verticals || []).map((v) => {
      const budget = Number(v.budget) || 0;
      const baseCpl = Number(v.base_cpl ?? summary.assumed_cpl ?? 0);
      const cpl = baseCpl ? baseCpl * multiplier : 0;
      const setRate = Number(v.set_rate ?? assumptions.set_rate ?? 0);
      const issueRate = Number(v.issue_rate ?? assumptions.issue_rate ?? 0);
      const demoRate = Number(v.demo_rate ?? assumptions.demo_rate ?? 0);
      const netCloseRate = Number(v.net_close_rate ?? assumptions.net_close_rate ?? 0);
      const avgJobSize = Number(v.avg_job_size ?? assumptions.avg_job_size ?? 0);
      const leads = cpl ? budget / cpl : 0;
      const setAppointments = leads * setRate;
      const issues = setAppointments * issueRate;
      const demos = issues * demoRate;
      const netSold = demos * netCloseRate;
      const netRevenue = netSold * avgJobSize;
      return {
        name: v.name || 'Unknown',
        budget,
        cpl,
        leads,
        setAppointments,
        issues,
        demos,
        netSold,
        avgJobSize,
        netRevenue,
        setRate,
        issueRate,
        demoRate,
        netCloseRate,
      };
    });

    const totals = rows.reduce(
      (acc, row) => {
        acc.budget += row.budget;
        acc.leads += row.leads;
        acc.setAppointments += row.setAppointments;
        acc.issues += row.issues;
        acc.demos += row.demos;
        acc.netSold += row.netSold;
        acc.netRevenue += row.netRevenue;
        return acc;
      },
      { budget: 0, leads: 0, setAppointments: 0, issues: 0, demos: 0, netSold: 0, netRevenue: 0 }
    );
    totals.avgCpl = totals.leads ? totals.budget / totals.leads : null;
    totals.avgJobSize = totals.netSold ? totals.netRevenue / totals.netSold : null;
    totals.costOfMarketingPct = totals.netRevenue ? totals.budget / totals.netRevenue : null;

    return { rows, totals, assumptions, scenarioMultiplier: multiplier };
  }, [projection.scenario, projection.summary]);

  const onScenarioChange = (_e, value) => {
    if (!value) return;
    const params = new URLSearchParams(location.search);
    params.set('scenario', value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setProjection((prev) => ({ ...prev, scenario: value }));
  };

  const handleExportZipCsv = useCallback(() => {
    const rows = (demographics.features || []).map((feature) => {
      const props = feature?.properties || {};
      return {
        zip: props.zip || '',
        territory: props.territory || '',
        city: props.city || '',
        state: props.state || '',
        population: props.population ?? '',
        status: props.passes_filter === false ? 'filtered_out' : 'included',
      };
    });
    if (!rows.length) return;

    const escapeCsv = (value) => {
      const str = String(value ?? '');
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const header = ['zip', 'territory', 'city', 'state', 'population', 'status'];
    const lines = [
      header.join(','),
      ...rows.map((row) => header.map((key) => escapeCsv(row[key])).join(',')),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `projection-zips-${client?.slug || clientSlug || 'client'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [client?.slug, clientSlug, demographics.features]);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Backdrop
        open={isLoadingProjection && !hasFetchedOnce}
        sx={{ color: 'white', zIndex: (theme) => theme.zIndex.drawer + 1, backdropFilter: 'blur(2px)' }}
      >
        <Stack spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress color="inherit" size={32} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Loading projections…
          </Typography>
        </Stack>
      </Backdrop>
      <Helmet>
        <title>Client View | {config.SITE.title}</title>
      </Helmet>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'center', xs: 'flex-start' }, justifyContent: 'space-between' }}
          >
            <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Box
                component="img"
                src={leadGurusLogo}
                alt="Lead Gurus"
                sx={{
                  height: 56,
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.2))',
                }}
              />
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, borderColor: 'divider' }} />
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                {client?.client_logo_url || client?.client_logo_file ? (
                  <Box
                    component="img"
                    src={client?.client_logo_url || client?.client_logo_file}
                    alt={client?.name || 'Client logo'}
                    sx={{
                      height: 52,
                      width: 'auto',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.16))',
                      bgcolor: 'rgba(0,0,0,0.03)',
                      borderRadius: 1.5,
                      p: 0.75,
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 52,
                      height: 52,
                      bgcolor: 'primary.main',
                      color: 'common.white',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    {client?.name ? client.name.slice(0, 2).toUpperCase() : 'CL'}
                  </Avatar>
                )}
                <Stack spacing={0.5} sx={{ minWidth: 260 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    Client Projections
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Prepared by Lead Gurus for
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {client?.name || 'Client'}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={projection.scenario}
                  onChange={onScenarioChange}
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontWeight: 700,
                      px: 1.75,
                      py: 0.5,
                      fontSize: '0.8rem',
                      borderColor: 'rgba(0,0,0,0.12)',
                      color: 'text.primary',
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      bgcolor: '#16b363',
                      color: 'common.white',
                      borderColor: '#16b363',
                    },
                    '& .MuiToggleButton-root.Mui-selected:hover': {
                      bgcolor: '#16b363',
                    },
                  }}
                >
                  <ToggleButton value="low">Low</ToggleButton>
                  <ToggleButton value="base">Base</ToggleButton>
                  <ToggleButton value="high">High</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {shareError && <Alert severity="error">{shareError}</Alert>}

        {!verticals.selections.length && (
          <Alert severity="info">
            Missing projection inputs. Open the generator and share again to populate this view.
          </Alert>
        )}

        {projection.summary ? (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              {(() => {
                const summary = projection.summary || {};
                const assumptions = projectionResults.assumptions || {};
                const totals = projectionResults.totals || {};
                const budget = Number(summary.budget || totals.budget || 0);
                const dailyBudget = budget ? budget / 30 : null;
                const currentScenario = projection.scenario || 'base';
                const leads = Number.isFinite(totals.leads) ? Math.round(totals.leads) : 0;
                const cpl = totals.avgCpl;
                const setAppointments = Number.isFinite(totals.setAppointments) ? Math.round(totals.setAppointments) : '—';
                const issues = Number.isFinite(totals.issues) ? Math.round(totals.issues) : '—';
                const demos = Number.isFinite(totals.demos) ? Math.round(totals.demos) : '—';
                const dealsSold = Number.isFinite(totals.netSold) ? Math.round(totals.netSold) : null;
                const avgJobSize = totals.avgJobSize || assumptions.avg_job_size || null;
                const netRevenue = Number.isFinite(totals.netRevenue) ? totals.netRevenue : null;
                const costMarketingPct =
                  totals.costOfMarketingPct != null && Number.isFinite(totals.costOfMarketingPct)
                    ? `${(totals.costOfMarketingPct * 100).toFixed(1)}%`
                    : '—';
                const setRateLabel = formatRate(assumptions.set_rate, 0);
                const issueRateLabel = formatRate(assumptions.issue_rate, 0);
                const demoRateLabel = formatRate(assumptions.demo_rate, 0);
                const closeRateLabel = formatRate(assumptions.net_close_rate, 0);

                return [
                  {
                    label: 'Total budget',
                    value: formatCurrency(budget),
                    sub: dailyBudget ? `~${formatCurrency(dailyBudget)}/day` : null,
                  },
                  {
                    label: `Leads & CPL (${currentScenario})`,
                    value: `${Number.isFinite(leads) ? leads : '—'} leads / ${cpl ? formatCurrency(cpl) : '—'}`,
                    sub: 'Est. leads / Blended CPL',
                  },
                  {
                    label: 'Set appointments',
                    value: setAppointments,
                    sub: setRateLabel ? `Set rate ${setRateLabel}` : null,
                  },
                  { label: 'Issues', value: issues, sub: issueRateLabel ? `Issue rate ${issueRateLabel}` : null },
                  { label: 'Demos', value: demos, sub: demoRateLabel ? `Demo rate ${demoRateLabel}` : null },
                  {
                    label: 'Net sold',
                    value: dealsSold == null ? '—' : dealsSold.toLocaleString(),
                    sub: closeRateLabel ? `Net close rate ${closeRateLabel}` : null,
                  },
                  {
                    label: 'Avg job size',
                    value: avgJobSize ? formatCurrency(avgJobSize) : '—',
                    sub: 'Avg by vertical',
                  },
                  { label: 'Net revenue', value: netRevenue == null ? '—' : formatCurrency(netRevenue), sub: null },
                  { label: 'Cost of marketing', value: costMarketingPct, sub: null },
                ];
              })().map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.label}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary" variant="body2">
                      {item.label}
                    </Typography>
                    <Typography variant="h6">{item.value ?? '—'}</Typography>
                    {item.sub ? (
                      <Typography color="text.secondary" variant="caption">
                        {item.sub}
                      </Typography>
                    ) : null}
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {projectionResults.rows.length ? (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    '& th, & td': { px: 0.75, py: 0.75, whiteSpace: 'nowrap', fontSize: 12 },
                    '& th:first-of-type, & td:first-of-type': { pl: 2 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Vertical</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>CPL</TableCell>
                      <TableCell>Set</TableCell>
                      <TableCell>Set%</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>Iss%</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>Demo%</TableCell>
                      <TableCell>Net sold</TableCell>
                      <TableCell>Close%</TableCell>
                      <TableCell>Avg job</TableCell>
                      <TableCell>Net rev</TableCell>
                      <TableCell>CoM%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectionResults.rows.map((row) => {
                      const costMarketingVal =
                        row.netRevenue && row.budget ? `${((row.budget / row.netRevenue) * 100).toFixed(1)}%` : '—';
                      return (
                        <TableRow key={row.name}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{formatCurrency(row.budget)}</TableCell>
                          <TableCell>{Number.isFinite(row.leads) ? Math.round(row.leads) : '—'}</TableCell>
                          <TableCell>{row.cpl ? formatCurrency(row.cpl) : '—'}</TableCell>
                          <TableCell>{Number.isFinite(row.setAppointments) ? Math.round(row.setAppointments) : '—'}</TableCell>
                          <TableCell>{formatRate(row.setRate)}</TableCell>
                          <TableCell>{Number.isFinite(row.issues) ? Math.round(row.issues) : '—'}</TableCell>
                          <TableCell>{formatRate(row.issueRate)}</TableCell>
                          <TableCell>{Number.isFinite(row.demos) ? Math.round(row.demos) : '—'}</TableCell>
                          <TableCell>{formatRate(row.demoRate)}</TableCell>
                          <TableCell>{Number.isFinite(row.netSold) ? Math.round(row.netSold) : '—'}</TableCell>
                          <TableCell>{formatRate(row.netCloseRate)}</TableCell>
                          <TableCell>{row.avgJobSize ? formatCurrency(row.avgJobSize) : '—'}</TableCell>
                          <TableCell>{row.netRevenue ? formatCurrency(row.netRevenue) : '—'}</TableCell>
                          <TableCell>{costMarketingVal}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}

            {demographics.features?.length ? (
              <ProjectionZipMap features={demographics.features} territories={demographics.territories} />
            ) : (
              <Alert icon={<WarningAmberIcon />} severity="info">
                No map data available. Regenerate projections to include ZIP boundaries.
              </Alert>
            )}

            {demographics.territories?.length ? (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="subtitle2">Territory Zip Code and Filter Analysis</Typography>
                    </Stack>
                    <Button variant="outlined" size="small" onClick={handleExportZipCsv}>
                      Export ZIP CSV
                    </Button>
                  </Stack>
                  <Divider sx={{ mb: 1 }} />
                  <Table size="small" sx={{ tableLayout: 'auto', minWidth: 800 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Territory</TableCell>
                        <TableCell>Zip count</TableCell>
                        <TableCell>Population (raw → filtered)</TableCell>
                        <TableCell>Median income</TableCell>
                        <TableCell>Median home value</TableCell>
                        <TableCell>Min income</TableCell>
                        <TableCell>Min home value</TableCell>
                        <TableCell>Min SFH%</TableCell>
                        <TableCell>Min ownership%</TableCell>
                        <TableCell>Population retained%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {demographics.territories.map((row, idx) => (
                        <TableRow key={`${row.territory}-${idx}`}>
                          <TableCell>{row.territory || 'No territory label'}</TableCell>
                          <TableCell>
                            {row.zip_count}
                            {row.zip_count_filtered != null ? ` → ${row.zip_count_filtered}` : ''}
                          </TableCell>
                          <TableCell>
                            {row.population != null ? row.population.toLocaleString() : '—'} {' \u2192 '}{' '}
                            {row.population_filtered != null ? row.population_filtered.toLocaleString() : '—'}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(row.median_income_filtered ?? row.median_income ?? '—')}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(row.median_home_value_filtered ?? row.median_home_value ?? '—')}
                          </TableCell>
                          <TableCell>{formatCurrency(row.min_household_income)}</TableCell>
                          <TableCell>{formatCurrency(row.min_home_value)}</TableCell>
                          <TableCell>{formatPercent(row.min_sfh_percentage, 0)}</TableCell>
                          <TableCell>{formatPercent(row.min_home_ownership_percentage, 0)}</TableCell>
                          <TableCell>
                            {row.population_retained_pct != null ? `${row.population_retained_pct}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {demographics.meta && demographics.meta.filter_found === false && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No saved client territory zip filter found. Applying default floors based on zip medians.
                    </Alert>
                  )}
                  {demographics.meta && demographics.meta.filter_found && demographics.meta.filter_effective === false && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Filter thresholds were present but did not exclude any zips.
                    </Alert>
                  )}
                </Box>
              </Paper>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress />
            <Typography color="text.secondary">Projections are loading. This may take a moment.</Typography>
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default PublicProjectionsClientView;
